import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTenantBranding } from '../lib/useTenantBranding.js'
import { useSessionStore } from '../stores/session.store.js'
import { useCartStore } from '../stores/cart.store.js'
import { useActiveOrderStore } from '../stores/active-order.store.js'
import { getMyTableOrder } from '../lib/api.js'
import { useTheme, effectiveTheme, toggleTheme } from '../lib/theme.js'
import { useStadiumMode, useSeatStore, cleanSeatPart } from '../lib/stadium.js'

/**
 * Branded header del guest PWA — rediseño 2026-07-22 (Gustavo: "está muy
 * ocupado").
 *
 * Antes competían: logo + nombre del tenant, "NINTAIGO", el branchName (que en
 * SAVIS es literalmente "SAVI's Coffee GDL — Av. México 2305A" → DUPLICABA el
 * nombre), la mesa, y DOS botones (Mi orden + Cerrar y salir).
 *
 * Ahora:
 *   [◧ Tenant]         [🧾 Mi orden] [☀️/🌙] [⋯]
 *       Mesa M1
 * El nombre del negocio va UNA vez; el contexto se reduce a la MESA. "Mi orden"
 * es la acción principal; el sol/luna alterna claro↔oscuro VISIBLE (como
 * POS/ADMIN); el menú ⋯ guarda lo destructivo ("Cerrar y salir").
 */
export function NintaiGOHeader({
  slug,
  tableCode,
  branchId: _branchId,
}: {
  slug?: string | undefined
  tableCode?: string | null | undefined
  customerName?: string | null | undefined
  /** Aceptado por compat con los callers; ya no se muestra la sucursal. */
  branchId?: string | null | undefined
}) {
  const navigate = useNavigate()
  const session = useSessionStore((s) => s.session)
  const hasSession = session !== null
  const clearSession = useSessionStore((s) => s.clear)
  const clearCart = useCartStore((s) => s.clear)
  const branding = useTenantBranding(slug ?? null)
  const tenantName = branding?.name ?? 'NintaiGO'
  const [theme] = useTheme()
  const [menuOpen, setMenuOpen] = useState(false)
  const [myOrderBusy, setMyOrderBusy] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)
  // Modo estadio: en vez de "Para llevar" mostramos el asiento; sin selector de tema.
  const stadium = useStadiumMode()
  const seat = useSeatStore((s) => (slug ? s.bySlug[slug] ?? null : null))

  // Cerrar el menú ⋯ al tocar fuera.
  useEffect(() => {
    if (!menuOpen) return
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [menuOpen])

  function handleExit(): void {
    clearSession()
    clearCart()
    useActiveOrderStore.getState().clear()
    navigate(slug ? `/r/${slug}` : '/', { replace: true })
  }

  // En takeout el tableCode es "TAKEOUT-…"; sólo mostramos mesa real.
  const isTakeout = (tableCode ?? '').toUpperCase().startsWith('TAKEOUT')
  const isTableSession = hasSession && session?.orderMode !== 'TAKEOUT'

  async function goToMyOrder(): Promise<void> {
    if (!session || myOrderBusy) return
    setMyOrderBusy(true)
    try {
      const r = await getMyTableOrder(session.token)
      navigate(r.ok && r.data?.id ? `/r/${slug}/order/${r.data.id}` : `/r/${slug}/menu`)
    } finally {
      setMyOrderBusy(false)
    }
  }

  // Tema efectivo (resuelve 'auto' contra el SO) → decide el ícono: si se ve
  // oscuro, el botón ofrece SOL (→claro); si se ve claro, LUNA (→oscuro).
  const effDark = effectiveTheme(theme) === 'dark'

  return (
    <header className="ng-header">
      <Link to={slug ? `/r/${slug}/menu` : '/'} className="ng-logo" aria-label={tenantName}>
        <img src="/favicon.svg" alt="" className="mark" />
        <span className="ng-id">
          <span className="ng-id__tenant">{tenantName}</span>
          {tableCode && !isTakeout && <span className="ng-id__ctx">Mesa {tableCode}</span>}
          {isTakeout && !stadium && <span className="ng-id__ctx">Para llevar</span>}
          {stadium && (
            <span className="ng-id__ctx">
              {seat
                ? `Sec ${cleanSeatPart(seat.section)} · Fila ${cleanSeatPart(seat.row)} · Asiento ${cleanSeatPart(seat.seat)}`
                : 'Pide desde tu asiento'}
            </span>
          )}
        </span>
      </Link>

      <div className="ng-actions">
        {hasSession && isTableSession && (
          <button type="button" className="ng-myorder" disabled={myOrderBusy} onClick={goToMyOrder}>
            {myOrderBusy ? '…' : '🧾 Mi orden'}
          </button>
        )}
        {!stadium && <button
          type="button"
          className="ng-iconbtn"
          onClick={() => toggleTheme(theme)}
          aria-label={effDark ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
          title={effDark ? 'Tema claro' : 'Tema oscuro'}
        >
          <span aria-hidden>{effDark ? '☀️' : '🌙'}</span>
        </button>}
        {hasSession && (
          <div className="ng-menu" ref={menuRef}>
            <button
              type="button"
              className="ng-more"
              aria-label="Más opciones"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              ⋯
            </button>
            {menuOpen && (
              <div className="ng-menu__pop" role="menu">
                <button
                  type="button"
                  className="ng-menu__item ng-menu__item--danger"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); handleExit() }}
                >
                  Cerrar y salir
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
